/**
 * 웹과 데스크톱의 차이를 흡수하는 단일 지점.
 *
 * 렌더러 코드 어디에도 `if (isElectron)` 분기가 흩어지지 않게 한다.
 * 새 네이티브 기능이 필요하면 이 인터페이스에 메서드를 추가하고
 * 웹 폴백을 함께 구현한다 — 폴백 없이 추가하면 웹이 조용히 깨진다.
 */
/** 데스크톱 창의 현재 설정. 앱 시작 시 한 번 읽어 UI에 반영한다. */
export interface DesktopState {
  readonly alwaysOnTop: boolean;
  readonly miniMode: boolean;
  readonly autoStart: boolean;
  /** 설치된 앱 버전. 웹에서는 null. */
  readonly appVersion: string | null;
  /** 내려받아 둔 새 버전. 없으면 null. */
  readonly updateReady: string | null;
  /**
   * 전역 보스키 등록이 성공했는지.
   *
   * 성공했으면 렌더러는 창 단위 핸들러를 걸지 않는다. 두 경로가 같이
   * 살아 있으면 한 번 누른 게 두 번 처리된다.
   */
  readonly bossKeyGlobal: boolean;
}

export interface PlatformBridge {
  readonly kind: 'web' | 'desktop';

  notify(title: string, body: string): void;
  /** 트레이/독 아이콘의 안 읽은 개수. 0이면 지운다. */
  setBadge(count: number): void;

  /**
   * 보스키를 등록하고 해제 함수를 돌려준다.
   *
   * 데스크톱은 앱이 백그라운드여도 잡히지만 웹은 탭에 포커스가 있을 때만
   * 동작한다. 이 차이는 흡수할 수 없으므로 UI에서 안내한다.
   */
  registerBossKey(handler: () => void): () => void;

  setAlwaysOnTop(on: boolean): void;
  setMiniMode(on: boolean): void;
  minimize(): void;
  /** OS 로그인 시 트레이로만 자동 시작. */
  setAutoStart(on: boolean): void;

  /**
   * 위장 모드 진입/해제를 알린다.
   *
   * 창 제목은 렌더러가 document.title로 바꾸지만, 트레이 툴팁과 배지는
   * 메인 프로세스만 건드릴 수 있다. 위장 중에 트레이에 "속닥 · 3개"가
   * 떠 있으면 위장이 아니다.
   */
  setStealth(on: boolean): void;

  /** 저장된 창 설정. 웹에서는 전부 false. */
  getDesktopState(): Promise<DesktopState>;

  /**
   * 트레이 메뉴로 창 설정이 바뀔 때 알림.
   *
   * 데스크톱에서만 실제로 호출된다. 이게 없으면 트레이에서 미니 모드를
   * 켰을 때 창은 좁아지지만 UI의 토글은 꺼진 채로 남는다.
   */
  onDesktopStateChanged?(handler: (state: DesktopState) => void): () => void;

  /** 새 버전을 내려받아 두었을 때. 데스크톱에서만 호출된다. */
  onUpdateReady?(handler: (version: string) => void): () => void;
  /** 내려받아 둔 업데이트를 설치하고 재시작한다. */
  installUpdate(): void;

  readonly secureStore: SecureStore;
}

export interface SecureStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

declare global {
  interface Window {
    /** preload가 contextBridge로 주입한다. 웹에서는 undefined. */
    sokdaq?: PlatformBridge;
  }
}

/** 데스크톱에서만 되는 기능. 웹에서 호출하면 조용히 아무 일도 없다. */
function desktopOnly(): void {
  /* 웹에서는 창을 제어할 수 없다. */
}

const webSecureStore: SecureStore = {
  // 웹에는 OS 키체인이 없다. localStorage는 XSS에 노출되지만
  // 여기 담기는 건 방 하나에 대한 재접속 토큰이고, 그 방에 이미
  // 들어와 있는 사람만 탈취할 수 있는 값이다.
  async get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      // 시크릿 모드나 스토리지 차단 환경.
      return null;
    }
  },
  async set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* 저장 못 해도 이번 세션은 계속 쓸 수 있다. */
    }
  },
  async remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* 위와 같다. */
    }
  },
};

const BOSS_KEY = { code: 'KeyX', ctrl: true, shift: true } as const;

/**
 * 창에 포커스가 있을 때 동작하는 보스키.
 *
 * 웹에서는 이게 전부다. 데스크톱에서는 전역 단축키와 함께 쓴다 —
 * 다른 앱이 Ctrl+Shift+X를 선점했으면 globalShortcut 등록이 실패하는데,
 * 그때 이 핸들러가 없으면 보스키가 아예 죽는다.
 */
function attachLocalBossKey(handler: () => void): () => void {
  const onKeyDown = (event: KeyboardEvent) => {
    if (
      event.code === BOSS_KEY.code &&
      event.ctrlKey === BOSS_KEY.ctrl &&
      event.shiftKey === BOSS_KEY.shift
    ) {
      event.preventDefault();
      handler();
    }
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}

/**
 * 한 번의 키 입력이 두 경로로 들어와도 한 번만 처리한다.
 *
 * 창을 아주 짧게 잡는 것이 핵심이다. 중복 전달은 같은 키 입력에서 나오므로
 * IPC 한 홉(보통 몇 ms) 안에 도착한다. 반면 사람이 일부러 두 번 누르는
 * 간격은 최소 100ms 정도다. 그 사이에 선을 그으면 중복만 걸러진다.
 *
 * 넉넉하게 잡았다가 "잠깐 열어 확인하고 바로 닫기"가 막히는 걸 실제로 봤다.
 */
function once(handler: () => void, windowMs = 20): () => void {
  let lastAt = 0;
  return () => {
    const now = Date.now();
    if (now - lastAt < windowMs) return;
    lastAt = now;
    handler();
  };
}

const webBridge: PlatformBridge = {
  kind: 'web',

  notify(title, body) {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  },

  setBadge(count) {
    /*
     * 웹에는 트레이가 없다. 설치형(PWA)이면 Badging API가 있고, 아니면
     * 할 수 있는 게 없다.
     *
     * 탭 제목은 건드리지 않는다. 제목은 useWindowTitle이 위장 여부까지
     * 함께 판단해서 정하므로, 여기서도 쓰면 두 곳이 서로 덮어쓴다.
     */
    const badging = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (count > 0) void badging.setAppBadge?.(count).catch(() => {});
    else void badging.clearAppBadge?.().catch(() => {});
  },

  registerBossKey: attachLocalBossKey,

  setAlwaysOnTop: desktopOnly,
  setMiniMode: desktopOnly,
  minimize: desktopOnly,
  setAutoStart: desktopOnly,
  setStealth: desktopOnly,
  installUpdate: desktopOnly,

  async getDesktopState() {
    // 웹은 브라우저가 알아서 최신을 내려주므로 업데이트 개념이 없다.
    return {
      alwaysOnTop: false,
      miniMode: false,
      autoStart: false,
      appVersion: null,
      updateReady: null,
      bossKeyGlobal: false,
    };
  },

  secureStore: webSecureStore,
};

/**
 * 현재 실행 환경에 맞는 브리지.
 *
 * 모듈 로드 시점에 한 번만 정한다. preload는 스크립트 실행 전에
 * 주입을 끝내므로 나중에 바뀔 일이 없다.
 */
function resolvePlatform(): PlatformBridge {
  const native = window.sokdaq;
  if (native === undefined) return webBridge;

  return {
    ...native,
    /*
     * 전역 단축키와 창 단축키를 함께 건다.
     *
     * globalShortcut은 다른 앱이 같은 조합을 쓰고 있으면 등록에 실패한다.
     * 그때도 창에 포커스가 있으면 보스키가 동작해야 한다 — 전역 등록만
     * 믿으면 조용히 기능이 사라진다.
     */
    registerBossKey(handler) {
      /*
       * 두 경로를 항상 함께 건다.
       *
       * 전역 등록이 성공했다고 창 단위 핸들러를 떼면 안 된다.
       * globalShortcut.register()가 true를 돌려주고 isRegistered()도 true인데
       * 콜백은 한 번도 불리지 않는 환경이 있다(창 관리자가 전역 핫키를
       * 전달하지 않는 경우 — Wayland 등). 그 상태에서 창 단위 핸들러까지
       * 떼면 보스키가 완전히 죽는다.
       *
       * 전역 단축키가 정상 동작하는 플랫폼에서는 OS가 키를 가로채므로
       * 렌더러까지 오지 않아 애초에 겹치지 않는다. 그래도 둘 다 들어오는
       * 경우를 대비해 짧은 창으로 중복만 걸러낸다.
       */
      const guarded = once(handler);
      const detachNative = native.registerBossKey(guarded);
      const detachLocal = attachLocalBossKey(guarded);

      return () => {
        detachNative();
        detachLocal();
      };
    },
  };
}

export const platform: PlatformBridge = resolvePlatform();

export const isDesktop = platform.kind === 'desktop';
